import { useState } from "react";
import jsPDF from "jspdf";
import { useParams } from "wouter";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Building2, FileText, Download, Lock, CheckCircle, Clock, AlertCircle, FileCode } from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(valor: number): string {
  return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(dateStr: string | Date | null): string {
  if (!dateStr) return "-";
  const d = new Date(typeof dateStr === "string" ? dateStr + "T00:00:00" : dateStr);
  return d.toLocaleDateString("pt-BR");
}

function statusBadge(status: string) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    pendente: { label: "Pendente", variant: "outline" },
    pago: { label: "Pago", variant: "default" },
    vencido: { label: "Vencido", variant: "destructive" },
    parcial: { label: "Parcial", variant: "secondary" },
  };
  const s = map[status] || { label: status, variant: "outline" };
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

function downloadBase64(base64: string, filename: string, mimeType: string) {
  const byteChars = atob(base64);
  const byteArr = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArr], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Componente principal ─────────────────────────────────────────────────────

export default function Financeiro() {
  const params = useParams<{ pedidoId: string; nfeNumero: string }>();
  const pedidoId = parseInt(params.pedidoId || "0", 10);
  const nfeNumero = parseInt(params.nfeNumero || "0", 10);

  const [senha, setSenha] = useState("");
  const [senhaConfirmada, setSenhaConfirmada] = useState("");
  const [senhaErro, setSenhaErro] = useState("");

  // Query principal — só executa após confirmar a senha
  const { data, isLoading, error, refetch } = trpc.financeiro.getCobranca.useQuery(
    { pedidoId, numeroNfe: nfeNumero, senha: senhaConfirmada },
    {
      enabled: !!senhaConfirmada,
      retry: false,
    }
  );

  // Query para download do XML
  const { data: xmlData, refetch: fetchXml } = trpc.financeiro.downloadXmlNfe.useQuery(
    { pedidoId, numeroNfe: nfeNumero, senha: senhaConfirmada },
    { enabled: false, retry: false }
  );

  const utils = trpc.useUtils();

  const handleSenha = (e: React.FormEvent) => {
    e.preventDefault();
    if (!senha.trim()) {
      setSenhaErro("Digite a senha do pedido.");
      return;
    }
    setSenhaErro("");
    setSenhaConfirmada(senha);
  };

  const downloadXml = async () => {
    const result = await fetchXml();
    if (result.data?.xml) {
      const blob = new Blob([result.data.xml], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `NFe_${nfeNumero}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const downloadDanfe = async () => {
    try {
      const result = await utils.financeiro.downloadDanfeVenda.fetch({ pedidoId, senha: senhaConfirmada });
      downloadBase64(result.base64, result.filename, "application/pdf");
    } catch (err: any) {
      alert(err?.message || "DANFE não disponível.");
    }
  };

  const downloadRelatorio = async () => {
    if (!data) return;
    try {
      const result = await utils.financeiro.gerarRelatorioPedido.fetch({ pedidoId, numeroNfe: nfeNumero, senha: senhaConfirmada });
      const link = document.createElement("a");
      link.href = result.url;
      link.download = `pedido_${pedidoId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      refetch();
    } catch (err: any) {
      alert(err?.message || "Não foi possível gerar o PDF do pedido.");
    }
  };

  function gerarPdfPedido(pedido: any) {
    const doc = new jsPDF();
    const lm = 14;
    let y = 20;
    const line = () => { doc.setDrawColor(200); doc.line(lm, y, 196, y); y += 4; };
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text(`Pedido #${pedido.id}`, lm, y); y += 10;
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    line();
    doc.setFont('helvetica', 'bold'); doc.text('EMITENTE', lm, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Raz\u00e3o Social: ${pedido.emitenteNome || '-'}`, lm, y); doc.text(`Nome Fantasia: ${pedido.emitenteFantasia || '-'}`, 110, y); y += 5;
    doc.text(`CNPJ: ${pedido.emitenteCnpj || '-'}`, lm, y); doc.text(`UF: ${pedido.emitenteUf || '-'}`, 110, y); y += 6;
    line();
    doc.setFont('helvetica', 'bold'); doc.text('DESTINAT\u00c1RIO', lm, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Raz\u00e3o Social: ${pedido.destinatarioRazao || '-'}`, lm, y); doc.text(`Nome Fantasia: ${pedido.destinatarioFantasia || '-'}`, 110, y); y += 5;
    doc.text(`CNPJ: ${pedido.destinatarioCnpj || '-'}`, lm, y); doc.text(`Cidade/UF: ${pedido.destinatarioCidade || '-'} / ${pedido.destinatarioUf || '-'}`, 110, y); y += 5;
    const endParts = [pedido.destinatarioLogradouro, pedido.destinatarioNumero, pedido.destinatarioBairro, pedido.destinatarioCep].filter(Boolean);
    if (endParts.length > 0) { doc.text(`Endere\u00e7o: ${endParts.join(', ')}`, lm, y); y += 5; }
    y += 1; line();
    doc.setFont('helvetica', 'bold'); doc.text('CONFIGURA\u00c7\u00c3O DO OR\u00c7AMENTO', lm, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Tipo de Or\u00e7amento: ${pedido.tipoOrcamentoNome || '-'}`, lm, y); doc.text(`Condi\u00e7\u00e3o de Pagamento: ${pedido.condicaoPagamentoDesc || '-'}`, 110, y); y += 5;
    doc.text(`Tipo de Frete: ${pedido.tipoFrete || '-'}`, lm, y); doc.text(`Tipo de Entrega: ${pedido.tipoEntrega || '-'}`, 110, y); y += 5;
    doc.text(`Transportadora: ${pedido.transportadora || '-'}`, lm, y); doc.text(`Tag: ${pedido.tag || '-'}`, 110, y); y += 5;
    doc.text(`Status: ${pedido.status || '-'}`, lm, y); doc.text(`Data: ${pedido.criadoEm ? new Date(pedido.criadoEm).toLocaleDateString('pt-BR') : '-'}`, 110, y); y += 6;
    line();
    doc.setFont('helvetica', 'bold'); doc.text(`ITENS DO PEDIDO (${(pedido.itens || []).length})`, lm, y); y += 6;
    doc.setFillColor(240, 240, 240); doc.rect(lm, y - 4, 182, 6, 'F');
    doc.text('C\u00f3digo', lm + 1, y); doc.text('Descri\u00e7\u00e3o', 35, y); doc.text('Qtd.', 95, y); doc.text('Cx.Mst', 110, y); doc.text('ST', 128, y); doc.text('Vl. Unit.', 143, y); doc.text('Total', 168, y); y += 5;
    doc.setFont('helvetica', 'normal');
    let totalGeral = 0;
    (pedido.itens || []).forEach((item: any) => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(String(item.productCod || '-'), lm + 1, y);
      const desc = doc.splitTextToSize(String(item.productDescription || '-'), 55);
      doc.text(desc, 35, y);
      doc.text(parseFloat(item.quantidade || 0).toFixed(0), 95, y);
      doc.text(String(item.qtdCaixaMaster || '-'), 110, y);
      doc.text(`R$ ${Number(item.icmsStValor || 0).toFixed(2)}`, 128, y);
      doc.text(`R$ ${Number(item.valorUnitario || 0).toFixed(2)}`, 143, y);
      doc.text(`R$ ${Number(item.valorTotal || 0).toFixed(2)}`, 168, y);
      totalGeral += Number(item.valorTotal || 0);
      y += Math.max(5, desc.length * 5);
    });
    y += 2; line();
    doc.setFont('helvetica', 'bold');
    doc.text(`TOTAL GERAL: R$ ${totalGeral.toFixed(2)}`, 168, y, { align: 'right' });
    doc.save(`pedido_${pedido.id}.pdf`);
  }

  const downloadGnrePdf = async () => {
    try {
      const result = await utils.financeiro.downloadGnrePdf.fetch({ pedidoId, senha: senhaConfirmada, cacheBuster: Date.now() });
      downloadBase64(result.base64, result.filename, "application/pdf");
    } catch (err: any) {
      alert(err?.message || "PDF da GNRE não disponível.");
    }
  };

  const downloadBoletoPorId = async (boletoId: number) => {
    try {
      const result = await utils.financeiro.downloadBoletosPdf.fetch({ pedidoId, senha: senhaConfirmada });
      // downloadBoletosPdf retorna ZIP; para boleto individual usamos o mesmo endpoint
      downloadBase64(result.base64, result.filename, "application/zip");
    } catch (err: any) {
      alert(err?.message || "Boleto não disponível.");
    }
  };

  const downloadTodosBoletos = async () => {
    try {
      const result = await utils.financeiro.downloadBoletosPdf.fetch({ pedidoId, senha: senhaConfirmada });
      downloadBase64(result.base64, result.filename, "application/zip");
    } catch (err: any) {
      alert(err?.message || "Boletos não disponíveis.");
    }
  };

  // ── Tela de senha ──────────────────────────────────────────────────────────

  if (!senhaConfirmada || (error && (error as any)?.data?.code === "UNAUTHORIZED")) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center space-y-2">
            <div className="flex justify-center">
              <div className="bg-blue-100 p-3 rounded-full">
                <Lock className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <CardTitle className="text-xl">Portal do Pedido</CardTitle>
            <CardDescription>
              Pedido #{pedidoId} — NF-e {nfeNumero}
              <br />
              Digite a senha do pedido para acessar os documentos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSenha} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Senha do pedido"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  autoFocus
                />
                {(senhaErro || (error && (error as any)?.data?.code === "UNAUTHORIZED")) && (
                  <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {senhaErro || "Senha incorreta. Tente novamente."}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full">
                Acessar
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // ── Erro genérico ──────────────────────────────────────────────────────────

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
            <CardTitle>Pedido não encontrado</CardTitle>
            <CardDescription>
              Verifique o link ou entre em contato com o emitente.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // ── Conteúdo principal ─────────────────────────────────────────────────────

  const parcelasComBoleto = data.parcelas.filter((p: any) => p.temBoleto);
  const totalPedido = data.parcelas.reduce((total: number, parcela: any) => total + Number(parcela.valorParcela || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">

        {/* Cabeçalho — emitente e destinatário */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            <span className="font-semibold text-gray-800">{data.emitenteNome}</span>
          </div>
          <span className="text-sm text-muted-foreground">{data.destinatarioNome}</span>
        </div>

        {/* NF-e + Pedido + Downloads */}
        {data.nfe && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-base">
                  NF-e Nº {data.nfe.numeroNfe} · Pedido #{pedidoId}
                </CardTitle>
              </div>
              <p className="text-sm text-muted-foreground">
                Valor total do pedido: {formatCurrency(totalPedido)} · Emissão: {formatDate(data.nfe.dataEmissao)}
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {data.nfe.temXml && (
                  <Button size="sm" variant="outline" onClick={downloadXml}>
                    <FileCode className="h-4 w-4 mr-1" />
                    Baixar XML
                  </Button>
                )}
                {data.nfe.temDanfe && (
                  <Button size="sm" variant="outline" onClick={downloadDanfe}>
                    <FileText className="h-4 w-4 mr-1" />
                    Baixar DANFE
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={downloadRelatorio}>
                  <Download className="h-4 w-4 mr-1" />
                  Baixar Pedido
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* GNRE */}
        {data.gnres?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  <CardTitle className="text-base">GNRE</CardTitle>
                </div>
                <Button size="sm" variant="outline" onClick={downloadGnrePdf}>
                  <Download className="h-4 w-4 mr-1" />
                  Baixar PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.gnres.map((gnre: any) => (
                <div key={gnre.id} className="border rounded-lg p-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">UF</p>
                      <p className="font-medium">{gnre.ufFavorecida}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Valor</p>
                      <p className="font-medium">{formatCurrency(gnre.valorTotal)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Vencimento</p>
                      <p className="font-medium">{formatDate(gnre.dataVencimento)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Status</p>
                      <p className="font-medium capitalize">{gnre.status}</p>
                    </div>
                  </div>
                  {gnre.codigoBarras && (
                    <div className="mt-2">
                      <p className="text-muted-foreground text-xs">Código de Barras</p>
                      <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1 break-all">{gnre.codigoBarras}</p>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Parcelas */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-500" />
                <CardTitle className="text-base">Parcelas</CardTitle>
              </div>
              {parcelasComBoleto.length > 0 && (
                <Button size="sm" variant="outline" onClick={downloadTodosBoletos}>
                  <Download className="h-4 w-4 mr-1" />
                  Baixar todos boletos
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.parcelas.map((p: any) => (
                <div
                  key={p.id}
                  className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700">
                        {p.numeroParcela}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{formatCurrency(p.valorParcela)}</p>
                        <p className="text-xs text-muted-foreground">
                          Venc. {formatDate(p.dataVencimento)} · {p.tipoPagamento || "—"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.status === "pago" && (
                        <p className="text-xs text-green-600">
                          Recebido: {formatCurrency(p.valorRecebido || p.valorParcela)} em {formatDate(p.dataRecebimento)}
                        </p>
                      )}
                      {statusBadge(p.status)}
                      {p.temBoleto && (
                        <Button size="sm" variant="outline" onClick={() => downloadBoletoPorId(p.boletoId)}>
                          <Download className="h-3 w-3 mr-1" />
                          Boleto
                        </Button>
                      )}
                    </div>
                  </div>
                  {p.boletoLinhaDigitavel && (
                    <div className="mt-2 ml-11">
                      <p className="text-muted-foreground text-xs">Código de Barras</p>
                      <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1 break-all">{p.boletoLinhaDigitavel}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
